import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import Container from '@/components/Container';
import Button from '@/components/Button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import FieldError from '@/components/FieldError';
import { enableMfa, setupMfa, type SessionResponse } from '@/services/auth/auth.service';
import { useCompleteLogin } from '../useCompleteLogin';
import { useMfaTexts } from '../mfaTexts';

/**
 * Mandatory MFA enrolment, shown on first login (or after an admin reset). Two
 * phases:
 *   1. scan the QR / enter the secret in an authenticator app, then confirm a code;
 *   2. store the one-time backup codes, then continue into the app.
 *
 * The short-lived `mfaToken` arrives via router state; a refresh loses it and
 * bounces to /login. Copy comes from the CMS with code defaults as a fallback
 * (see mfaTexts.ts).
 */
export default function MfaSetup() {
  const location = useLocation();
  const mfaToken = (location.state as { mfaToken?: string } | null)?.mfaToken;

  const [setup, setSetup] = useState<{
    qrDataUrl: string | null;
    secret: string;
    otpauthUrl: string;
  } | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [pendingSession, setPendingSession] = useState<SessionResponse | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);

  const completeLogin = useCompleteLogin();
  const navigate = useNavigate();
  const t = useMfaTexts();
  const codeInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (setup) codeInputRef.current?.focus();
  }, [setup]);

  // The short-lived setup token expired (or is otherwise invalid): the user must
  // sign in again to restart enrolment. Distinguished from a wrong code, which
  // the user can simply retry.
  const isSessionError = (msg?: string | null) => !!msg && /session/i.test(msg);

  useEffect(() => {
    if (!mfaToken) return;
    let active = true;
    (async () => {
      const { data, error: err } = await setupMfa(mfaToken);
      if (!active) return;
      if (err || !data) {
        if (isSessionError(err?.message)) setSessionExpired(true);
        else setSetupError(err?.message || t.setupErrorFallback);
        return;
      }
      setSetup(data);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mfaToken]);

  if (!mfaToken) return <Navigate to="/login" replace />;

  const onConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    const { data, error: err } = await enableMfa(mfaToken, code.trim());
    setLoading(false);
    if (err || !data?.token) {
      if (isSessionError(err?.message)) {
        setSessionExpired(true);
        return;
      }
      setError(err?.message || t.verifyErrorFallback);
      return;
    }
    setPendingSession({ token: data.token, user: data.user });
    setBackupCodes(data.backupCodes);
  };

  // Setup session expired — dead-end the flow with a clear message + re-login.
  if (sessionExpired) {
    return (
      <Container className="h-full">
        <div className="col-span-12 flex min-h-0 items-center justify-center py-4">
          <div className="flex max-h-[90dvh] w-full max-w-[430px] flex-col items-start overflow-auto rounded-2xl bg-white p-8 shadow-lg">
            <h2 className="mb-2 font-haffer text-sh-30-38 font-bold leading-9 tracking-tight text-black-200">
              {t.setupTitle}
            </h2>
            <p className="mb-6 font-haffer text-sh-sm text-dark-grey">{t.setupExpired}</p>
            <Button type="button" theme="solid" showChevron={false} onClick={() => navigate('/login')}>
              {t.backToSignInCta}
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  // Phase 2 — show backup codes once, then continue.
  if (backupCodes && pendingSession) {
    return (
      <Container className="h-full">
        <div className="col-span-12 flex min-h-0 items-center justify-center py-4">
          <div className="flex max-h-[90dvh] w-full max-w-[430px] flex-col items-start overflow-auto rounded-2xl bg-white p-8 shadow-lg">
            <h2 className="mb-2 font-haffer text-sh-30-38 font-bold leading-9 tracking-tight text-black-200">
              {t.backupTitle}
            </h2>
            <p className="mb-6 font-haffer text-sh-sm text-dark-grey">{t.backupDescription}</p>
            <div className="bg-light-grey mb-6 grid w-full grid-cols-2 gap-2 rounded-lg p-4 font-mono text-sh-sm">
              {backupCodes.map((c) => (
                <span key={c} className="tracking-wider text-black-200">
                  {c}
                </span>
              ))}
            </div>
            <Button type="button" theme="solid" showChevron={false} onClick={() => completeLogin(pendingSession)}>
              {t.backupContinueCta}
            </Button>
          </div>
        </div>
      </Container>
    );
  }

  // Phase 1 — QR + confirmation.
  return (
    <Container className="h-full">
      <div className="col-span-12 flex min-h-0 items-center justify-center py-4">
        <div className="flex max-h-[90dvh] w-full max-w-[430px] flex-col items-start overflow-auto rounded-2xl bg-white p-8 shadow-lg">
          <h2 className="mb-2 font-haffer text-sh-30-38 font-bold leading-9 tracking-tight text-black-200">
            {t.setupTitle}
          </h2>
          <p className="mb-6 font-haffer text-sh-sm text-dark-grey">{t.setupDescription}</p>

          {setupError && <FieldError message={setupError} />}

          {!setupError && !setup && <p className="mb-6 font-haffer text-sh-sm text-dark-grey">{t.setupPreparing}</p>}

          {setup && (
            <>
              {setup.qrDataUrl ? (
                <img
                  src={setup.qrDataUrl}
                  alt="Authenticator QR code"
                  className="border-light-grey mb-4 h-[200px] w-[200px] self-center rounded-lg border"
                />
              ) : null}

              <p className="mb-1 font-haffer text-sh-xs text-dark-grey">{t.setupManualKeyLabel}</p>
              <code className="bg-light-grey mb-6 block w-full break-all rounded-lg p-3 font-mono text-sh-xs text-black-200">
                {setup.secret}
              </code>

              <form className="flex w-full flex-col items-start" onSubmit={onConfirm}>
                <div className="mb-6 w-full">
                  <Label>
                    <p className="mb-2 font-haffer text-sh-xs text-dark-grey">{t.setupCodeLabel}</p>
                  </Label>
                  <Input
                    ref={codeInputRef}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder={t.codePlaceholder}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  {error && <FieldError message={error} />}
                </div>

                <Button type="submit" theme="solid" className="mb-4" showChevron={false} disabled={loading}>
                  {loading ? t.loadingLabel : t.setupCta}
                </Button>
              </form>
            </>
          )}

          <Link
            className="text-center font-haffer text-sh-xs font-bold text-people-teal-600 hover:!text-people-blue-600"
            to="/login">
            {t.backToSignInCta}
          </Link>
        </div>
      </div>
    </Container>
  );
}
